import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Icon,
  Switch,
  Text,
} from "@prismicio/editor-ui"
import type { Editor } from "@tiptap/core"
import type { CSSProperties, MutableRefObject } from "react"

import { PassthroughTrigger } from "./PassthroughTrigger"
import {
  isFirstOrLastColumn,
  isHeaderColumnToggled,
  isHeaderRowToggled,
  isSecondColumn,
  isSingleColumn,
} from "./utils"

interface TableColumnMenuProps {
  editor: Editor
  style?: CSSProperties
  open: boolean
  onOpenChange: (open: boolean) => void
  floatingStyle: CSSProperties
  triggerRef: (node: HTMLElement | null) => void
  targetTriggerElementRef: MutableRefObject<HTMLElement | undefined>
}

export function TableColumnMenu(props: TableColumnMenuProps) {
  const {
    editor,
    open,
    onOpenChange,
    floatingStyle,
    triggerRef,
    targetTriggerElementRef,
  } = props

  const hasHeaderColumn = isHeaderColumnToggled(editor)
  const hasSingleCol = isSingleColumn(editor)
  const selection = isFirstOrLastColumn(editor)

  function onDelete(event: Event) {
    const headerRowToggled = isHeaderRowToggled(editor)

    editor.commands.deleteColumn()
    onOpenChange(false)

    // Prevent header column for a single column table
    if (hasSingleCol && hasHeaderColumn) {
      toggleHeaderColumn(event)

      // Remove header row if it was not previously toggled
      if (!headerRowToggled) editor.commands.toggleHeaderRow()
    }
  }

  function toggleHeaderColumn(event: Event) {
    event.preventDefault() // prevent menu from closing
    editor.commands.toggleHeaderColumn()
  }

  function onInsertLeft() {
    // Will move the header column from the current first column to the new first column
    if (hasHeaderColumn && selection.isFirstColumn) {
      editor
        .chain()
        .toggleHeaderColumn()
        .addColumnBefore()
        .toggleHeaderColumn()
        .run()
    } else {
      editor.commands.addColumnBefore()
    }

    onOpenChange(false)
  }

  function onInsertRight() {
    editor.commands.addColumnAfter()
    onOpenChange(false)
  }

  function onMoveLeft() {
    const isMovingHeaderColumn = hasHeaderColumn && isSecondColumn(editor)
    const command = editor.chain()

    if (isMovingHeaderColumn) {
      command.toggleHeaderColumn().moveColumn("left").toggleHeaderColumn()
    } else {
      command.moveColumn("left")
    }

    command.run()
    onOpenChange(false)
  }

  function onMoveRight() {
    const isMovingHeaderColumn = hasHeaderColumn && selection.isFirstColumn
    const command = editor.chain()

    if (isMovingHeaderColumn) {
      command.toggleHeaderColumn().moveColumn("right").toggleHeaderColumn()
    } else {
      command.moveColumn("right")
    }

    command.run()
    onOpenChange(false)
  }

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal>
      <DropdownMenuTrigger>
        <PassthroughTrigger
          ref={triggerRef}
          targetTriggerElementRef={targetTriggerElementRef}
          style={floatingStyle}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="bottom" sideOffset={4}>
        <DropdownMenuLabel>
          <Text color="grey11" variant="small">
            Column options
          </Text>
        </DropdownMenuLabel>
        {selection.isFirstColumn && (
          <DropdownMenuItem
            startIcon={<Icon name="viewDay" />}
            onSelect={toggleHeaderColumn}
            disabled={hasSingleCol}
          >
            <Box gap={12} alignItems="center">
              Header Column
              <Switch size="extraSmall" checked={hasHeaderColumn} />
            </Box>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          startIcon={<Icon name="arrowBack" />}
          onSelect={onInsertLeft}
        >
          Insert Left
        </DropdownMenuItem>
        <DropdownMenuItem
          startIcon={<Icon name="arrowForward" />}
          onSelect={onInsertRight}
        >
          Insert Right
        </DropdownMenuItem>
        <DropdownMenuItem
          startIcon={<Icon name="moveLeft" />}
          onSelect={onMoveLeft}
          disabled={selection.isFirstColumn}
        >
          Move Left
        </DropdownMenuItem>
        <DropdownMenuItem
          startIcon={<Icon name="moveRight" />}
          onSelect={onMoveRight}
          disabled={selection.isLastColumn}
        >
          Move Right
        </DropdownMenuItem>
        <DropdownMenuItem
          startIcon={<Icon name="delete" />}
          onSelect={onDelete}
          disabled={hasSingleCol}
        >
          Delete Column
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
